/*
 * ****************************************************************
 * File: mediaSelector.js
 * Project: CPaaS
 * Owner: Radisys
 *
 * Copyright - 2021 Radisys Corporation.
 * All rights reserved. No part of this software may be used or
 * reproduced in any form by any means without the prior written
 * permission of Radisys Corporation.
 *
 * Author: Prathik Pai
 *
 * ****************************************************************
 */
import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppBar,
  Divider,
  Grid,
  IconButton,
  Slider,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import VolumeUp from "@material-ui/icons/VolumeUp";
import PropTypes from "prop-types";
import SpeakerIcon from "@material-ui/icons/Speaker";
import CameraIcon from "@material-ui/icons/Camera";
import MicIcon from "@material-ui/icons/Mic";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import Switch from "@material-ui/core/Switch";

const useStyles = makeStyles({
  list: {
    width: 300,
  },
  fullList: {
    width: "auto",
  },
  appbar: {
    width: "100%",
  },
  toolbar: {
    fontSize: 16,
    flexGrow: 1,
    textTransform: "capitalize",
  },
  bar: {
    height: "30px",
    width: "10px",
    backgroundColor: "green",
    marginRight: "5px",
  },
  empty: {
    height: "30px",
    width: "10px",
    backgroundColor: "lightgray",
    marginRight: "5px",
  },
  sound: {
    width: "380px",
    hight: "5px",
  },
  cam: {
    width: "200px",
    height: "200px",
  },
  popover: {
    pointerEvents: "none",
  },
});

const MediaSelector = ({
  resolutionData,
  closePopOver,
  RTCUtils,
  session,
  hoverState,
  devices,
  onDeviceSelected,
  conferenceLayout,
  setConferenceLayout,
  onLayoutChange,
}) => {
  const classes = useStyles();
  const [setValue] = React.useState(0.5);
  const [, setAnchorEl] = React.useState(null);

  const [resolution, setResolution] = React.useState("SD");

  useEffect(() => {
    const obj = document.getElementById("remoteAudio");
    if (obj) {
      setValue(obj.volume);
    }
  }, []);

  useEffect(() => {
    // Grab the available devices
    if (hoverState && RTCUtils) {
      RTCUtils.getAVPermission();
    }
  }, []);
  const onSpeakerResetHandler = () => {
    if (RTCUtils) {
      const resetSpreaker = RTCUtils.resetAudioOutputDevice();
      if (resetSpreaker) {
        const speaker = RTCUtils.getSelectedAudioOutputDevice();
        onDeviceSelected("speaker", speaker.label);
      }
    }
  };

  const onMicResetHandler = () => {
    if (RTCUtils) {
      const resetMic = RTCUtils.resetAudioInputDevice();
      if (resetMic) {
        const input = RTCUtils.getSelectedAudioInputDevice();
        onDeviceSelected("input", input.label);
      }
    }
  };

  const onCameraResetHandler = () => {
    if (RTCUtils) {
      const resetCamera = RTCUtils.resetVideoInputDevice();
      if (resetCamera) {
        const camera = RTCUtils.getSelectedVideoInputDevice();
        onDeviceSelected("camera", camera.label);
      }
    }
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const onResolutionSelected = (event) => {
    if (RTCUtils && devices.camera?.length !== 0) {
      if (
        session.session &&
        session.session.getMaxResolutionSupported() !== undefined
      ) {
        const clientResolutionData = resolutionData.find(
          (resolutionObj) => resolutionObj.value === event.target.value
        );
        const supportedResolutionData = resolutionData.find(
          (resolutionObj) =>
            resolutionObj.value === session.session.getMaxResolutionSupported()
        );
        if (clientResolutionData.weight <= supportedResolutionData.weight) {
          RTCUtils.setCameraResolution(event.target.value);
          console.log("!!!! Resolution Set:", event.target.value);
          setResolution(event.target.value);
        } else {
          toast.success(
            `${event.target.value} not supported, since max supported resolution is ${supportedResolutionData.value}`
          );
        }
      } else {
        RTCUtils.setCameraResolution(event.target.value);
        setResolution(event.target.value);
      }
    }
  };

  useEffect(() => {
    if (RTCUtils) {
      if (RTCUtils.getSelectedCameraResolution() !== undefined) {
        setResolution(RTCUtils.getSelectedCameraResolution());
      }
    }
  }, []);

  return (
    <div
      style={{
        width: "300px",
        minHeight: devices.speaker?.length === 0 ? "350px" : "500px",
      }}
    >
      <div>
        <AppBar
          variant="outlined"
          position="sticky"
          color="transparent"
          className={classes.appbar}
        >
          <Toolbar variant="dense">
            <Typography
              className={classes.toolbar}
              style={{ marginLeft: "-16px", padding: "5px" }}
            >
              settings
            </Typography>
            <IconButton
              data-testid="iconbutton"
              edge="end"
              onClick={() => closePopOver()}
              aria-label="close"
              style={{ color: "tomato", border: "none", outline: "none" }}
            >
              <Tooltip title="Close" placement="bottom">
                <Close />
              </Tooltip>
            </IconButton>
          </Toolbar>
        </AppBar>
      </div>
      {devices.speaker?.length === 0 && devices.input?.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "18vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <Grid
          container
          spacing={5}
          style={{ padding: "10px" }}
          justify="center"
        >
          <div style={{ padding: "20px", width: "100%" }}>
            <Typography gutterBottom>Device settings</Typography>
            {devices.speaker?.length === 0 ? null : (
              <>
                <div style={{ display: "flex" }}>
                  <SpeakerIcon />
                  <Typography gutterBottom>Speakers</Typography>
                  <Tooltip title="Reset to Default">
                    <RotateLeftIcon
                      data-testid="onSpeakerResetHandler"
                      onClick={onSpeakerResetHandler}
                      style={{ marginLeft: "auto" }}
                    />
                  </Tooltip>
                </div>
                <TextField
                  data-testid="speaker-text"
                  key="Role"
                  size="small"
                  fullWidth
                  value={devices.selected.speaker}
                  onChange={(e) => onDeviceSelected("speaker", e.target.value)}
                  required
                  select
                  SelectProps={{ native: true }}
                  variant="outlined"
                >
                  {devices.speaker !== undefined
                    ? devices.speaker.map((option) => (
                        <option key={option.deviceID} value={option.label}>
                          {option.label}
                        </option>
                      ))
                    : "No speaker found"}
                </TextField>
                <div style={{ display: "flex", marginTop: "4px" }}>
                  <VolumeUp style={{ marginRight: "4px" }} />
                  <Slider
                    defaultValue={0.7}
                    aria-labelledby="continuous-slider"
                    min={0}
                    max={1}
                    step={0.001}
                  />
                </div>
              </>
            )}
          </div>

          <div style={{ padding: "20px", marginTop: "-32px", width: "100%" }}>
            <div style={{ display: "flex" }}>
              <MicIcon />
              <Typography gutterBottom>Microphone</Typography>
              <Tooltip title="Reset to Default">
                <RotateLeftIcon
                  data-testid="onMicResetHandler"
                  onClick={onMicResetHandler}
                  style={{ marginLeft: "auto" }}
                />
              </Tooltip>
            </div>
            <TextField
              key="Role"
              size="small"
              fullWidth
              value={devices.selected.input}
              onChange={(e) => onDeviceSelected("input", e.target.value)}
              required
              select
              SelectProps={{ native: true }}
              variant="outlined"
            >
              {devices.input !== undefined
                ? devices.input.map((option) => (
                    <option
                      key={option.deviceID}
                      value={option.label}
                      style={{
                        maxWidth: "90%",
                        height: "10%",
                        overflow: "auto",
                        textAlign: "right",
                      }}
                    >
                      {option.label}
                    </option>
                  ))
                : "No microphone found"}
            </TextField>
          </div>
          <div style={{ padding: "20px", marginTop: "-20px", width: "100%" }}>
            <div style={{ display: "flex" }}>
              <CameraIcon />
              <Typography gutterBottom>Camera</Typography>
              <Tooltip title="Reset to Default">
                <RotateLeftIcon
                  data-testid="onCameraResetHandler"
                  onClick={onCameraResetHandler}
                  style={{ marginLeft: "auto" }}
                />
              </Tooltip>
            </div>
            <TextField
              data-testid="camera-text"
              key="Role"
              size="small"
              fullWidth
              value={devices.selected.camera}
              onChange={(e) => onDeviceSelected("camera", e.target.value)}
              required
              select
              SelectProps={{ native: true }}
              variant="outlined"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            >
              {devices.camera !== undefined
                ? devices.camera.map((option) => (
                    <option key={option.deviceID} value={option.label}>
                      {option.label}
                    </option>
                  ))
                : "No cameras found"}
            </TextField>
          </div>

          <div
            style={{
              padding: "20px",
              marginTop: "-20px",
              width: "100%",
              marginBottom: "",
            }}
          >
            <div style={{ display: "flex" }}>
              <AspectRatioIcon />
              <Typography gutterBottom style={{ marginLeft: "3px" }}>
                Resolution
              </Typography>
            </div>
            <TextField
              key="Role"
              size="small"
              fullWidth
              value={resolution}
              onChange={(e) => onResolutionSelected(e)}
              required
              select
              SelectProps={{ native: true }}
              variant="outlined"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              disabled={session?.session?.isScreenShared()}
            >
              {resolutionData !== undefined
                ? resolutionData.map((option) => (
                    <option key={option.label} value={option.value}>
                      {option.label}
                    </option>
                  ))
                : "No resolution found"}
            </TextField>
          </div>
          <Divider fullWidth orientation="horizontal" light={false} />
          <div
            style={{
              padding: "20px",
              marginTop: "-20px",
              width: "100%",
              marginBottom: "",
            }}
          >
            <Typography gutterBottom>Layout Settings</Typography>
            <div style={{ display: "flex", flexDirection: "row" }}>
              <Typography style={{ marginTop: "5px" }}>
                {conferenceLayout ? "Disable" : "Enable"} Conference Layout
              </Typography>
              <Switch
                checked={conferenceLayout}
                onChange={(e) => onLayoutChange(e)}
                color="primary"
                name="checkedB"
                inputProps={{ "aria-label": "primary checkbox" }}
              />
            </div>
          </div>
        </Grid>
      )}
    </div>
  );
};

MediaSelector.propTypes = {
  hoverState: PropTypes.any,
  RTCUtils: PropTypes.any,
  session: PropTypes.any,
  closePopOver: PropTypes.any,
  devices: PropTypes.any,
  onDeviceSelected: PropTypes.any,
  resolutionData: PropTypes.any,
  conferenceLayout: PropTypes.any,
  setConferenceLayout: PropTypes.any,
  onLayoutChange: PropTypes.any,
};

export default MediaSelector;
